import React, { useRef, useState, useCallback, useMemo } from 'react';

import { useLocale } from '@adsk/alloy-react-locale';
import { useNotifications, NOTIFICATION_STATUSES } from '@adsk/alloy-react-notification';
import Button from '@adsk/nirvana-button';
import EmptyState from '@adsk/nirvana-empty-state';
import { useAnalytics } from '@adsk/nirvana-helpers';
import Modal from '@adsk/nirvana-modal';
import Tooltip from '@adsk/nirvana-tooltip';
import VirtualizedTree from '@adsk/nirvana-virtualized-tree';

import EditableNode from './EditableNode';
import {
  TreeNode,
  NodeLabel,
  TooltipNodeLabel,
  Menu,
  MenuIcon,
  ContentLoading,
  StyledFolderItemCheckbox,
  StyledFolderItem
} from './styles';
import {
  getFolderIcon,
  filterSelectedFolders,
  filterSelectedFolderKeys,
  getSelectedFolderPaths,
  getDefaultExpandedKeys,
  getMenuOptions as getDefaultMenuOptions,
  getMappedFolders,
  validateFolderName,
  getErrorStateProps,
  getIsAncestor,
  isPermissionLoaded,
  setT
} from '../../helpers';
import { MENU_TYPES, MENU_ACTIONS, NODE_CREATE_KEY, AnalyticsEvents } from '../../helpers/consts';
import { useService } from '../../hooks';
import { FolderTreeProps, FolderPickerRef, FolderType, MenuOptionType } from '../../types';

type VirtualizedTreeRef = typeof VirtualizedTree & {
  scrollToNode: (node: { urn: string }) => void;
  forceUpdate: () => void;
  handleExpandNode: (node: FolderType) => Promise<any>;
};

/**
 * FolderTree
 */
const FolderTree = React.forwardRef<FolderPickerRef, FolderTreeProps>(
  (
    {
      container,
      disabled,
      defaultLoadFolderKey,
      disabledKeys = [],
      defaultExpandedKeys,
      selectedKeys,
      cascadeSelection = false,
      defaultSelectedKeys = [],
      nodeValidator,
      nodeNameGenerator,
      multiple,
      fullPath,
      folderType,
      editable = false,
      menuType = MENU_TYPES.ACTION,
      menuOptionVisible = true,
      getMenuOptions = getDefaultMenuOptions,
      onCreate,
      onSelectNodesChange,
      itemRenderer,
      editItemRenderer,
      folderIconRenderer,
      ...rest
    },
    forwardedRef
  ) => {
    const menuRef = useRef<{
      show: (event: React.MouseEvent) => void;
      hide: () => void;
    }>(null);
    const treeRef = useRef<VirtualizedTreeRef>(null);

    const { T, locale } = useLocale(({ locale }) => require(`../../../locale/${locale}.json`));
    const { showNotification } = useNotifications();
    const tracker: any = useAnalytics();

    const {
      state: { folders, treeState, loading, hasError, resStatus: status },
      dispatch
    } = useService();
    const [selectedFolderKeys, setSelectedFolderKeys] = useState<string[]>(defaultSelectedKeys);
    const [menuNode, setMenuNode] = useState<FolderType | null>(null);
    const [menuOptions, setMenuOptions] = useState<MenuOptionType[]>([]);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [selectedFolderPaths, setSelectedFolderPaths] = useState({});
    const [menuLoading, setMenuLoading] = useState(false);

    const data = useMemo(() => getMappedFolders(folders, treeState), [folders, treeState]);
    const expandedNodeKeys = useMemo(
      () => getDefaultExpandedKeys(folders, defaultExpandedKeys, defaultLoadFolderKey),
      [folders, defaultExpandedKeys, defaultLoadFolderKey]
    );
    const selectedFolders = useMemo(
      () => filterSelectedFolders(folders, selectedFolderKeys),
      [folders, selectedFolderKeys]
    );
    const isDeepLinkError = useMemo(
      () => !loading && !hasError && folders.length === 0 && !!defaultLoadFolderKey,
      [loading, hasError, folders, defaultLoadFolderKey]
    );

    const getOptions = useCallback((node: FolderType) => getMenuOptions(node, menuOptionVisible), [menuOptionVisible]);
    const isNodeDisabled = useCallback(
      (node: FolderType) =>
        node.readonly || disabledKeys.includes(node.urn) || disabled || !!(nodeValidator?.(node) || {}).message,
      [disabledKeys, disabled, nodeValidator]
    );
    const isNodeExpandable = useCallback((node: FolderType) => !!node.hasSubfolders, []);
    const isNodeSelectable = useCallback((node: FolderType) => !node.readonly, []);

    const handleLoadTopFolders = useCallback(
      (folderUrn?: string, fullPath?: boolean, folderType?: string) => {
        if (folderType) {
          return dispatch.getTopFoldersForFolderType(folderType, folderUrn);
        }
        return dispatch.getTopFolders(folderUrn, fullPath);
      },
      [dispatch]
    );
    const handleExpandFolder = useCallback(
      (node: FolderType) => {
        // When show full folder path enabled, the parent nodes are shown at folder tree and all parent nodes are readonly (added by sdk when parsing)
        // There is no need to reload it's sub folder from API (And also no permission to do that).
        // We can resolve and return directly.
        if (node.readonly) return Promise.resolve();
        return dispatch.getFolder(node.urn);
      },
      [dispatch]
    );
    const handleSelectNodesChange = useCallback(
      (keys: string[]) => {
        setSelectedFolderKeys(keys);
        setSelectedFolderPaths((preValue) => getSelectedFolderPaths(folders, keys, preValue));
      },
      [folders]
    );

    // Init load folders and also pick / scroll to default folder if defaultLoadFolder existed
    const handleInitialLoadFolders = useCallback(
      async (defaultLoadFolder?: string, fullPath?: boolean, folderType?: string) => {
        try {
          const folders = await handleLoadTopFolders(defaultLoadFolder, fullPath, folderType);

          if (defaultLoadFolder && folders.length > 0) {
            // Select the loaded key by default
            setSelectedFolderKeys((keys) => (!keys.includes(defaultLoadFolder) ? [...keys, defaultLoadFolder] : keys));

            window.requestAnimationFrame(() => {
              treeRef.current?.scrollToNode({ urn: defaultLoadFolder });
            });
          }
        } catch (error) {
          // Swallow error
          console.error('Failed to load top folders.', error);
        }
      },
      [handleLoadTopFolders]
    );

    const handleScroll = useCallback(() => {
      // Hide context menu on scroll
      menuRef.current?.hide();
      setMenuNode(null);
    }, []);

    const handleOpenMenu = useCallback(
      ({ event, node }: { event: React.MouseEvent; node: FolderType }) => {
        if (!menuRef.current) return;
        // avoid trigger node selection
        event.stopPropagation();
        setMenuNode(node);
        if (isPermissionLoaded(node)) {
          setMenuOptions(getOptions(node));
        } else {
          setMenuLoading(true);
          dispatch
            .getFolderPermission(node.urn)
            .then((permissionActions) => {
              setMenuOptions(getOptions({ ...node, permissionActions }));
              setMenuLoading(false);
            })
            .catch((_ex) => {
              setMenuLoading(false);
            });
        }
        menuRef.current.show(event);
      },
      [getOptions, dispatch]
    );

    const handleAddNode = useCallback(
      (node: FolderType) => {
        if (!isNodeExpandable(node)) dispatch.updateTreeNode(node.urn, { hasSubfolders: true });

        return treeRef.current
          ?.handleExpandNode(node)
          .then(() => {
            dispatch.addPendingTreeNode(node.urn, nodeNameGenerator);
          })
          .catch((ex: any) => {
            console.error(`Failed to expand node.`, ex);
          });
      },
      [folders, isNodeExpandable, nodeNameGenerator]
    );

    const handleSelectMenu = useCallback(
      (key: string) => {
        if (!menuNode) return;
        switch (key) {
          case MENU_ACTIONS.RENAME:
            dispatch.updateTreeNode(menuNode.urn, { isEditing: true });
            break;
          case MENU_ACTIONS.DELETE:
            setShowDeleteConfirm(true);
            break;
          case MENU_ACTIONS.CREATE:
            handleAddNode(menuNode);
            break;
          default:
            const selectedOption = menuOptions.find(({ value }) => value === key);
            if (selectedOption?.onClick) selectedOption.onClick(menuNode);
            break;
        }
      },
      [menuNode, menuOptions, handleAddNode]
    );

    const hideMenu = useCallback(
      (node: FolderType, checkContext: boolean) => {
        const menuTypes = checkContext ? [MENU_TYPES.BOTH, MENU_TYPES.CONTEXT] : [MENU_TYPES.BOTH, MENU_TYPES.ACTION];
        return !editable || node.readonly === true || !menuTypes.includes(menuType) || isNodeDisabled(node);
      },
      [editable, menuType, isNodeDisabled]
    );

    const handleContextMenu = useCallback(
      ({ event, node }: { event: React.MouseEvent; node: FolderType }) => {
        if (!hideMenu(node, true)) handleOpenMenu({ event, node });
      },
      [hideMenu, handleOpenMenu]
    );

    const handleCreate = (
      node: FolderType & {
        parentNodeKey: string;
      },
      newValue: string,
      isCancel?: boolean
    ) => {
      if (isCancel) {
        dispatch.removePendingTreeNode(node.parentNodeKey, node.urn, ['hasSubfolders']);
        return Promise.resolve();
      }

      dispatch.updatePendingTreeNode(node.parentNodeKey, node.urn, {
        isEditing: false,
        isDisabled: true,
        title: newValue
      });

      return dispatch
        .createFolder(node.parentNodeKey, newValue)
        .then((folder) => {
          onCreate && onCreate(folder as FolderType);
          tracker.trackEvent(AnalyticsEvents.folderCreate, {
            'Folder Type': 'Permitted',
            From: 'Folder Picker'
          });
        })
        .catch((ex) => {
          const status = ex.response?.status;
          const code = ex.response?.data?.error?.code || ex.response?.data?.code || ex.response?.code;
          let i18nKey = 'error.createFailure';
          if (status === 404) {
            i18nKey = 'error.createNoParentFolder';
          } else if (status === 423) {
            i18nKey = 'error.createFolderLocked'; // folder is locked by WIP
          } else if (status === 400 && code === 'ERR_FOLDER_PATH_TOO_DEPTH') {
            i18nKey = 'error.createMaxLevel';
          }
          showNotification({ status: NOTIFICATION_STATUSES.ERROR, message: T.translate(i18nKey) });
        })
        .finally(() => {
          dispatch.removePendingTreeNode(node.parentNodeKey, node.urn, ['hasSubfolders']);
        });
    };

    const handleRename = (node: FolderType, newValue: string, isCancel?: boolean) => {
      const action = node[VirtualizedTree.NODE_KEYS.NODE_ACTION_TYPE];
      if (action === NODE_CREATE_KEY) {
        return handleCreate(
          node as FolderType & {
            parentNodeKey: string;
          },
          newValue,
          isCancel
        );
      }

      if (newValue === node.title || isCancel) {
        dispatch.updateTreeNode(node.urn, {}, ['isEditing']);
        return Promise.resolve();
      }

      dispatch.updateTreeNode(
        node.urn,
        {
          isDisabled: true,
          title: newValue
        },
        ['isEditing']
      );

      return dispatch
        .updateFolder(node.urn, newValue)
        .then(() => {
          showNotification({
            status: NOTIFICATION_STATUSES.SUCCESS,
            message: T.translate('message.folderRenamed', { title: newValue })
          });
          tracker.trackEvent(AnalyticsEvents.folderOperate, {
            'Folder Type': 'Permitted',
            'Folder Count': 1,
            From: 'Folder Picker',
            Action: 'Rename'
          });
        })
        .catch((ex) => {
          const status = ex.response?.status;
          let i18nKey = 'error.renameFailure';
          if (status === 409) {
            i18nKey = 'error.duplicated';
          } else if (status === 400) {
            i18nKey = 'error.invalidName';
          } else if (status === 403) {
            i18nKey = 'error.notAllowed';
          } else if (status === 423) {
            i18nKey = 'error.folderLocked'; // folder is locked by WIP
          }

          showNotification({ status: NOTIFICATION_STATUSES.ERROR, message: T.translate(i18nKey) });
        })
        .finally(() => {
          dispatch.updateTreeNode(node.urn, {}, ['isDisabled', 'title', 'isEditing']);
        });
    };

    const handleRemove = (node: FolderType) => {
      dispatch.updateTreeNode(node.urn, { isDisabled: true });
      return dispatch
        .deleteFolder(node.urn)
        .then(() => {
          showNotification({
            status: NOTIFICATION_STATUSES.SUCCESS,
            message: T.translate('message.folderDeleted', { title: node.title })
          });
          // Remove selected keys if target nodes are deleted
          setSelectedFolderKeys((keys) => filterSelectedFolderKeys(folders, node.urn, keys, selectedFolderPaths));
          tracker.trackEvent(AnalyticsEvents.folderOperate, {
            'Folder Type': 'Permitted',
            'Folder Count': 1,
            From: 'Folder Picker',
            Action: 'Remove'
          });
        })
        .catch((ex) => {
          const status = ex.response?.status;
          let i18nKey = 'error.deleteError';
          if ([408, 504].includes(status)) {
            i18nKey = 'error.deleteTimeout';
          }
          showNotification({ status: NOTIFICATION_STATUSES.ERROR, message: T.translate(i18nKey) });
        })
        .finally(() => {
          dispatch.updateTreeNode(node.urn, {}, ['isDisabled']);
        });
    };

    const handleFolderItemClick = (isNodeSelected: boolean, folder: FolderType) => {
      const nextClickedFolders = selectedFolders.filter((item) => !getIsAncestor(folder, item));
      if (!isNodeSelected) {
        nextClickedFolders.push(folder);
      }
      const nextClickedFoldersKeys = nextClickedFolders.map((folder) => folder.urn);
      setSelectedFolderKeys(nextClickedFoldersKeys);
      setSelectedFolderPaths((preValue) => getSelectedFolderPaths(folders, nextClickedFoldersKeys, preValue));
      treeRef.current?.forceUpdate();
    };

    React.useEffect(() => {
      // check fullPath type here to wait it loaded from project API.
      if (typeof fullPath === 'boolean') handleInitialLoadFolders(defaultLoadFolderKey, fullPath, folderType);
    }, [defaultLoadFolderKey, fullPath, folderType, handleInitialLoadFolders]);

    React.useEffect(() => {
      dispatch.setSdkLocale(locale);
      setT(T);
    }, [locale, dispatch, T]);

    React.useEffect(() => {
      if (selectedKeys !== undefined) setSelectedFolderKeys(selectedKeys);
    }, [selectedKeys]);

    React.useEffect(() => {
      onSelectNodesChange?.(selectedFolderKeys, selectedFolders);
    }, [selectedFolderKeys, selectedFolders]);

    const renderActionMenu = ({ node }: { node: FolderType }) => {
      if (hideMenu(node, false)) return null;
      const hidden = isPermissionLoaded(node) && getOptions(node).length === 0;
      return <MenuIcon hidden={hidden} onClick={(event) => handleOpenMenu({ event, node })} />;
    };

    const renderViewItem = ({ node }: { node: FolderType }) => {
      let viewItem;
      if (itemRenderer) {
        viewItem = itemRenderer(node);
      } else {
        let message, popoverProps;
        if (node.readonly) {
          message = T.translate('error.noPermissionFolder');
        } else {
          const validation = nodeValidator?.(node);
          message = validation?.message;
          popoverProps = validation?.popoverProps;
        }
        const FolderIcon = getFolderIcon(node);
        viewItem = (
          <TreeNode>
            {folderIconRenderer ? folderIconRenderer(node, FolderIcon) : <FolderIcon style={{ marginRight: '4px' }} />}
            {message ? (
              <Tooltip content={message} maxWidth={320} {...popoverProps}>
                <TooltipNodeLabel>{node.title}</TooltipNodeLabel>
              </Tooltip>
            ) : (
              <NodeLabel tooltipMaxWidth={320} {...popoverProps}>
                {node.title}
              </NodeLabel>
            )}
          </TreeNode>
        );
      }

      if (!multiple || !cascadeSelection) {
        return (
          <>
            {viewItem}
            {renderActionMenu({ node })}
          </>
        );
      }

      // Manually render checkbox for multiple selection if cascadeSelection mode is true
      const isNodeSelected = selectedFolders.some((item) => item.urn === node.urn);
      const isAncestorClicked = selectedFolders.some((item) => getIsAncestor(item, node, true));
      const isDisabled = isNodeDisabled(node) || isAncestorClicked;
      return (
        <StyledFolderItem
          onClick={() => !isDisabled && handleFolderItemClick(isNodeSelected, node)}
          $disabled={isDisabled}
        >
          <StyledFolderItemCheckbox checked={isNodeSelected || isAncestorClicked} disabled={isDisabled} />
          {viewItem}
          {renderActionMenu({ node })}
        </StyledFolderItem>
      );
    };

    const renderEditItem = ({ node }: { node: FolderType }) => {
      if (editItemRenderer) {
        return editItemRenderer(node);
      }
      return (
        <EditableNode
          placeholder={T.translate('message.placeholder') as string}
          value={node.title}
          onValidate={(value) => validateFolderName(folders, node, value, treeState[node.urn]?.creatingNodes)}
          onChange={(value, isCancel) => handleRename(node, value, isCancel)}
        />
      );
    };

    const renderContextMenu = () => {
      return !editable ? null : (
        <Menu
          data-testid="folder-picker-menu"
          ref={menuRef}
          container={container}
          options={menuOptions}
          isLoading={menuLoading}
          onClick={handleSelectMenu}
        />
      );
    };

    const renderDeleteModal = () => {
      if (!showDeleteConfirm) return null;
      return (
        <Modal
          alert
          show
          confirmButtonAlert
          backdrop="static"
          size="small"
          cancelButtonLabel={T.translate('buttons.cancel')}
          confirmButtonLabel={T.translate('buttons.delete')}
          onHide={() => setShowDeleteConfirm(false)}
          onConfirm={() => {
            setShowDeleteConfirm(false);
            if (menuNode) handleRemove(menuNode);
          }}
          title={T.translate('message.deleteFolder')}
        >
          {T.translate('message.deleteFolderDesc', { title: menuNode?.title })}
        </Modal>
      );
    };

    const renderErrorState = () => {
      const { buttonLabel, onClick, ...rest } = getErrorStateProps({
        status,
        isDeepLinkError,
        onRetry: () => handleInitialLoadFolders(defaultLoadFolderKey, fullPath, folderType),
        onLoadTopFolders: () => handleLoadTopFolders(undefined, fullPath, folderType)
      });

      return (
        <EmptyState {...rest}>
          <Button variant="flat" onClick={onClick}>
            {buttonLabel}
          </Button>
        </EmptyState>
      );
    };

    return (
      <>
        {loading ? (
          <ContentLoading>
            {new Array(4).fill('').map((_, index) => (
              <React.Fragment key={index}>
                <rect x={0} height={12} rx={6} ry={6} y={index * 4 * 34} width="30%" />
                <rect x={0} height={12} rx={6} ry={6} y={(index * 4 + 1) * 34} width="40%" />
                <rect x={0} height={12} rx={6} ry={6} y={(index * 4 + 2) * 34} width="20%" />
                <rect x={0} height={12} rx={6} ry={6} y={(index * 4 + 3) * 34} width="50%" />
              </React.Fragment>
            ))}
          </ContentLoading>
        ) : hasError || isDeepLinkError ? (
          renderErrorState()
        ) : (
          <>
            <VirtualizedTree
              ref={(ref: any) => {
                treeRef.current = ref;

                if (forwardedRef) {
                  typeof forwardedRef === 'function' ? forwardedRef(ref) : (forwardedRef.current = ref);
                }
              }}
              scrollToAlignment="center"
              {...rest}
              showDisabledSelection
              valueKey="urn"
              childrenKey="folders"
              expandNodeFailureText={T.translate('error.loadFolders')}
              isNodeExpandable={isNodeExpandable}
              isNodeDisabled={isNodeDisabled}
              isNodeSelectable={isNodeSelectable}
              defaultExpandedKeys={expandedNodeKeys}
              viewItemRenderer={renderViewItem}
              editItemRenderer={renderEditItem}
              nodes={data}
              onExpandNode={handleExpandFolder}
              multiple={multiple && !cascadeSelection}
              selectedKeys={selectedFolderKeys}
              defaultSelectedKeys={defaultSelectedKeys}
              onSelectNodesChange={multiple && cascadeSelection ? undefined : handleSelectNodesChange}
              onContextMenu={handleContextMenu}
              onScroll={handleScroll}
            />
            {renderContextMenu()}
            {renderDeleteModal()}
          </>
        )}
      </>
    );
  }
);

FolderTree.displayName = 'FolderTree';

export default FolderTree;
